// BM-H4482 (Vanessa, 19 Oct 2026): "build the calculator separately but as a tool in Bonsai Pen".
// Late payment interest on its own: statutory interest and fixed sums under the 1998 Act, or a
// contract rate. The figures come from CaseMinder's fixed code (backend/hub/late_payment); this
// page saves nothing and drafts nothing.
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const field = "w-full bg-navy-dark border border-navy-border rounded px-2 py-1 text-xs text-txt";
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const blankRow = () => ({ reference: "", due_date: "", amount: "" });

const WARNINGS = {
  rate_tables_not_checked_to_this_date: "The rate tables were last checked on {checked}. Check Bank Rate has not changed since.",
  reference_rate_estimated: "* No reference rate in the table for that half-year yet: the latest one is used as an estimate.",
  no_reference_rate_take_advice: "An invoice started running before 2019: no rate in the table, interest shown as nil. Take advice.",
  over_five_years_limitation: "An invoice is more than five years past due. Claims generally have to be issued within six years (Limitation Act 1980, s5).",
  before_bank_rate_table_too_old: "Some days fall before the Bank Rate table starts (2 August 2018) and count at 0% base. Too old to rely on.",
};

export default function LatePaymentCalculator({ apiBase = API }) {
  const [workOutTo, setWorkOutTo] = useState(today());
  const [basis, setBasis] = useState("statutory");
  const [rate, setRate] = useState("");
  const [rows, setRows] = useState([blankRow()]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const setRow = (i, k, v) => setRows((cur) => cur.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${apiBase}/late-payment-calculate`, {
        work_out_to: workOutTo, basis, contract_rate: basis === "statutory" ? null : rate || null,
        invoices: rows.filter((r) => r.due_date && r.amount),
      });
      setResult(res.data.calculation);
    } catch (e) {
      setResult(null);
      toast.error(errorMessage(e, "Could not work the figures out."));
    } finally {
      setBusy(false);
    }
  };

  const t = result?.totals;
  const statutory = result?.basis === "statutory";
  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="late-payment-calculator">
      <h1 className="text-2xl font-semibold text-txt mb-1">Late payment interest calculator</h1>
      <p className="text-sm text-txt-muted mb-5">
        Business to business debts. Simple interest on a 365-day year, worked out per invoice and rounded once to the penny.
        Want the letter as well? Use <Link className="underline" to="/bonsai-pen/late-payment">Letter before claim</Link>.
      </p>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <label className="text-xs text-txt-muted">Work it out to
          <input type="date" className={field} value={workOutTo} onChange={(e) => setWorkOutTo(e.target.value)} data-testid="lpc-date" />
        </label>
        <label className="text-xs text-txt-muted">Interest
          <select className={field} value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="lpc-basis">
            <option value="statutory">Statutory (1998 Act)</option>
            <option value="contract_fixed">Contract: fixed % a year</option>
            <option value="contract_above_base">Contract: % above Bank Rate</option>
          </select>
        </label>
        {basis !== "statutory" && (
          <label className="text-xs text-txt-muted">{basis === "contract_fixed" ? "Rate % a year" : "Margin over Bank Rate %"}
            <input className={field} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="lpc-rate" />
          </label>
        )}
      </div>

      <table className="w-full text-xs text-txt mb-2">
        <thead><tr className="text-left text-txt-muted"><th>Invoice</th><th>Due date</th><th>Still owed £ (inc VAT)</th><th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><input className={field} value={r.reference} onChange={(e) => setRow(i, "reference", e.target.value)} data-testid={`lpc-ref-${i}`} /></td>
              <td><input type="date" className={field} value={r.due_date} onChange={(e) => setRow(i, "due_date", e.target.value)} data-testid={`lpc-due-${i}`} /></td>
              <td><input className={field} value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} data-testid={`lpc-amount-${i}`} /></td>
              <td><button type="button" title="Remove" className="text-txt-muted" onClick={() => setRows((cur) => cur.filter((_, j) => j !== i))}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-3 items-center mb-6">
        {rows.length < 20 && (
          <button type="button" className="text-xs underline text-txt-muted" onClick={() => setRows((cur) => [...cur, blankRow()])}>Add an invoice</button>
        )}
        <Button size="sm" onClick={run} disabled={busy} data-testid="lpc-run">Work it out</Button>
      </div>

      {result && (
        <div data-testid="lpc-result">
          <table className="w-full text-xs text-txt mb-3">
            <thead><tr className="text-left text-txt-muted"><th>Invoice</th><th>Owed</th><th>Due</th><th>Days late</th><th>Rate</th>
              <th>Interest</th><th>Per day</th>{statutory && <th>Fixed sum</th>}</tr></thead>
            <tbody>
              {result.invoices.map((r) => (
                <tr key={`${r.reference}-${r.due_date}`}>
                  <td>{r.reference}</td><td>{gbp(r.amount)}</td><td>{r.due_date}</td><td>{r.days_late}</td><td>{r.rate_label}</td>
                  <td>{gbp(r.interest)}</td><td>{gbp(r.daily)}</td>{statutory && <td>{gbp(r.fixed_sum)}</td>}
                </tr>
              ))}
              <tr className="font-semibold">
                <td>Total</td><td>{gbp(t.amount)}</td><td /><td /><td />
                <td>{gbp(t.interest)}</td><td>{gbp(t.daily)}</td>{statutory && <td>{gbp(t.fixed_sums)}</td>}
              </tr>
            </tbody>
          </table>
          <p className="text-sm text-txt" data-testid="lpc-total">
            Total claimed {gbp(t.claimed)}, then {gbp(t.daily)} a day.
          </p>
          {result.ignored?.length > 0 && (
            <p className="text-xs text-txt-muted">Left out: {result.ignored.map((x) => `${x.reference || "(no number)"} (${x.reason})`).join("; ")}.</p>
          )}
          <ul className="text-xs text-status-amber list-disc ml-5 mt-2">
            {(result.warnings || []).map((w) => (
              <li key={w}>{(WARNINGS[w] || w).replace("{checked}", result.rate_tables_checked_to)}</li>
            ))}
            {result.basis !== "statutory" && (
              <li>A contract rate replaces statutory interest only if it is a substantial remedy (ss8 and 9 of the 1998 Act). Take advice if it looks low.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
